import _ from 'lodash'
import { createSelector } from 'reselect'

import { paramSelector } from '../../utils/selectors'
import { RootState } from '../types'

import { DomainType, LocalizedDomainData } from './types'
import { generateStatesLoadingCode, generateTownsLoadingCode } from './utils'

type WithLanguage = {
  language: string
}

type WithDomains = {
  domains: DomainType[]
}

type WithDomain = {
  domain: DomainType
}

type WithCode = {
  code?: string
}

export const selectState = (state: RootState) => state.domains
export const selectData = createSelector(selectState, s => s.data)

export const isDomainLoading = createSelector(
  selectState,
  paramSelector<WithDomain>(),
  ({ loadingEntities }, { domain }) => loadingEntities[domain] || false
)

export const areDomainsLoading = createSelector(
  selectState,
  paramSelector<WithDomains>(),
  ({ loadingEntities }, { domains }) => _.some(domains, domain => loadingEntities[domain])
)

export const getDomainsLoading = createSelector(selectState, ({ loadingEntities }) => loadingEntities)

export const getDomains = createSelector(
  selectData,
  paramSelector<WithDomains & WithLanguage>(),
  (reducerData, { domains, language }) =>
    _.reduce(
      domains,
      (acc, domain) => {
        acc[domain] = reducerData[language]?.domains ? reducerData[language].domains[domain] : null
        return acc
      },
      {} as LocalizedDomainData['domains']
    )
)

export const getDomain = createSelector(
  selectData,
  paramSelector<WithDomain & WithLanguage>(),
  (reducerData, { domain, language }) =>
    reducerData[language]?.domains ? reducerData[language].domains[domain] : undefined
)

export const getStatesByCountryCode = createSelector(
  selectData,
  paramSelector<WithCode & WithLanguage>(),
  (reducerData, { code, language }) => {
    if (!code) return undefined
    return reducerData[language]?.states ? reducerData[language].states[code] : undefined
  }
)

export const getTownsByStateCode = createSelector(
  selectData,
  paramSelector<WithCode & WithLanguage>(),
  (reducerData, { code, language }) => {
    if (!code) return undefined
    return reducerData[language]?.towns ? reducerData[language].towns[code] : undefined
  }
)

export const areTownsLoading = createSelector(
  selectState,
  paramSelector<WithCode>(),
  ({ loadingEntities }, { code }) => {
    if (!code) return undefined
    return loadingEntities[generateTownsLoadingCode(code)] || false
  }
)

export const areStatesLoading = createSelector(
  selectState,
  paramSelector<WithCode>(),
  ({ loadingEntities }, { code }) => {
    if (!code) return false
    return loadingEntities[generateStatesLoadingCode(code)] || false
  }
)
